import { useState } from 'react'
import {
  PageHeader, Breadcrumbs, Toolbar, Button, Badge, StatCard, KpiStrip, KpiRow,
  Pagination, FormField, DataTable, Modal, MultiSelect,
  type Column, type BadgeVariant,
} from '@/components/ui'
import { IconSearch, IconSun, IconMoon } from '@/components/icons/Icons'
import { useTheme } from '@/hooks/useTheme'

/*
  UI Kit — catálogo vivo del design system. Muestra cada primitiva de
  src/components/ui/ con un ejemplo. Sirve para ver y copiar el patrón estándar.
  Ruta: /ui-kit
*/

const SEMAFORO: BadgeVariant[] = ['success', 'warning', 'danger', 'info', 'neutral']

const BADGES: BadgeVariant[] = [
  'borrador', 'publicado', 'destacado', 'producto', 'activo',
  'aceptada', 'rechazada', 'cancelado', 'completado', 'pausado',
  'ingreso', 'egreso', 'recurrente',
]

/* Las superficies y los textos del tema activo. Es la única pantalla donde un
   `background: var(--token)` inline es correcto: acá el color ES el contenido,
   no la presentación de otra cosa. */
const SUPERFICIES = ['--bg-body', '--bg-sidebar', '--bg-card', '--bg-card-hover',
  '--bg-subtle', '--bg-input', '--bg-table-header', '--border', '--border-light']

const VELOS = ['--veil-02', '--veil-04', '--veil-06', '--veil-10', '--veil-15', '--veil-25']

function Muestra({ token }: { token: string }) {
  return (
    <div className="ui-swatch">
      <span className="ui-swatch__color" style={{ background: `var(${token})` }} />
      <code>{token}</code>
    </div>
  )
}

interface DemoRow { id: number; nombre: string; estado: 'activo' | 'borrador'; monto: number }
const DEMO_ROWS: DemoRow[] = [
  { id: 1, nombre: 'Cliente Alfa', estado: 'activo', monto: 1200 },
  { id: 2, nombre: 'Cliente Beta', estado: 'borrador', monto: 450 },
  { id: 3, nombre: 'Cliente Gamma', estado: 'activo', monto: 3800 },
]

function Section({ title, snippet, children }: { title: string; snippet: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{title}</h3>
        <code style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{snippet}</code>
      </div>
      {children}
    </div>
  )
}

export default function UiKit() {
  const [tema, alternarTema] = useTheme()
  const [modalOpen, setModalOpen] = useState(false)
  const [texto, setTexto] = useState('')
  const [demoPage, setDemoPage] = useState(1)
  const [demoMulti, setDemoMulti] = useState<string[]>(['pendiente'])

  const columns: Column<DemoRow>[] = [
    { key: 'nombre', header: 'Nombre', render: (r) => <span style={{ fontWeight: 600 }}>{r.nombre}</span> },
    { key: 'estado', header: 'Estado', render: (r) => <Badge variant={r.estado === 'activo' ? 'activo' : 'borrador'}>{r.estado}</Badge> },
    { key: 'monto', header: 'Monto', align: 'right', render: (r) => `S/ ${r.monto.toLocaleString('es-PE')}` },
    {
      key: 'acc', header: 'Acciones', align: 'right',
      render: () => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary">Editar</Button>
          <Button size="sm" variant="danger">Borrar</Button>
        </div>
      ),
    },
  ]

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Configuración' }, { label: 'UI Kit' }]}
        meta="Catálogo del design system"
        subtitle="Componé páginas nuevas con estas primitivas — sin estilos inline."
        actions={
          <>
            {/* El conmutador vive acá además del menú del perfil: el UI Kit es
                donde se comprueba que una primitiva funciona en los DOS temas,
                y hacerlo obligaba a salir de la pantalla y volver. */}
            <Button variant="secondary" onClick={alternarTema}>
              {tema === 'dark' ? <IconSun size={15} /> : <IconMoon size={15} />}
              {tema === 'dark' ? 'Ver en claro' : 'Ver en oscuro'}
            </Button>
            <Button variant="primary" onClick={() => setModalOpen(true)}>Abrir Modal</Button>
          </>
        }
      />

      <Section title="Tokens de superficie" snippet="var(--bg-card)">
        <p className="ui-nota">
          Los componentes nombran <strong>semánticas</strong> (el rol: <code>--bg-card</code>,
          <code>--text-primary</code>), nunca primitivas (<code>--slate-200</code>): una
          primitiva no dice para qué sirve, así que no puede cambiar con el tema.
          La regla completa está en <code>prd/core/05-Design-System.md</code>.
        </p>
        <div className="ui-swatches">
          {SUPERFICIES.map((t) => <Muestra key={t} token={t} />)}
        </div>
      </Section>

      <Section title="Velos" snippet="var(--veil-06)">
        <p className="ui-nota">
          Fondos y bordes translúcidos. Reemplazan a los <code>rgba(255,255,255,x)</code>,
          que sólo funcionan sobre fondo oscuro: en claro el velo es tinta oscura y más
          floja, porque a igual opacidad pesa bastante más.
        </p>
        <div className="ui-swatches">
          {VELOS.map((t) => <Muestra key={t} token={t} />)}
        </div>
      </Section>

      <Section title="Jerarquía de texto" snippet="var(--text-secondary)">
        <p className="ui-nota">
          Tres niveles y basta: un cuarto no se distingue de los otros tres, y lo que
          necesita menos peso que <code>muted</code> necesita salir de la pantalla.
        </p>
        <p className="text-primary">Primario — el dato que se vino a leer.</p>
        <p className="text-secondary">Secundario — la etiqueta que lo acompaña.</p>
        <p className="text-muted">Apagado — lo que está ahí por si hace falta.</p>
      </Section>

      <Section title="Semáforo de estado" snippet='<Badge variant="danger" />'>
        <p className="ui-nota">
          <strong>success</strong> terminó bien · <strong>warning</strong> va a necesitar
          atención · <strong>danger</strong> está mal ahora · <strong>info</strong> es un
          dato, no un estado. Los badges de dominio se definen sobre estos, no con un hex
          propio.
        </p>
        <div className="ui-row">
          {SEMAFORO.map((v) => <Badge key={v} variant={v}>{v}</Badge>)}
        </div>
      </Section>

      <Section title="Breadcrumbs" snippet='<PageHeader breadcrumbs={[…]} meta />'>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
          Las páginas se identifican por su <strong>ruta</strong>, no por un título grande.
          Toda página nueva encabeza con <code>breadcrumbs</code>; <code>title</code> queda
          solo para pantallas legadas sin migrar.
        </p>
        <Breadcrumbs
          items={[{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Pagos Programados' }]}
          meta="23 pagos"
        />
      </Section>

      <Section title="Franja de KPIs (compacta)" snippet='<KpiStrip><KpiRow moneda><StatCard /></KpiRow></KpiStrip>'>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
          Los KPIs de un listado son contexto, no el contenido: van compactos para que la
          tabla siga siendo lo primero que se ve. El chip de moneda evita sumar S/ con $.
        </p>
        <KpiStrip>
          <KpiRow moneda="PEN">
            <StatCard label="Vencido" value="S/ 1,144.59" tone="danger" />
            <StatCard label="Por pagar" value="S/ 3,983.66" tone="warning" />
            <StatCard label="Base imp." value={<>S/ 8,120.00<div className="kpi-sub">IGV: S/ 1,461.60</div></>} />
          </KpiRow>
          <KpiRow moneda="USD">
            <StatCard label="Por cobrar" value="$95.90" tone="success" />
          </KpiRow>
        </KpiStrip>
      </Section>

      <Section title="Barra de filtros" snippet='<div className="filters-bar">'>
        <div className="filters-bar" style={{ marginBottom: 0 }}>
          <div className="filters-bar__search">
            <IconSearch size={14} className="filters-bar__search-icon" />
            <input className="form-input" placeholder="Buscar…" />
          </div>
          <select className="form-input" style={{ width: 175 }}><option>Todos los estados</option></select>
          <select className="form-input" style={{ width: 110 }}><option>Moneda</option></select>
          <Button variant="secondary" size="sm" className="filters-bar__clear">Limpiar filtros</Button>
        </div>
      </Section>

      <Section
        title="Filtro de selección múltiple"
        snippet='<MultiSelect placeholder options value onChange />'
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <MultiSelect
            placeholder="Todos los estados"
            options={[
              { value: 'pendiente', label: 'Pendiente' },
              { value: 'en_progreso', label: 'En Progreso' },
              { value: 'completada', label: 'Completada' },
              { value: 'bloqueada', label: 'Bloqueada' },
            ]}
            value={demoMulti}
            onChange={setDemoMulti}
          />
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
            Lista vacía = sin filtro. Dentro de un filtro suma (O); entre filtros, restringe (Y).
          </span>
        </div>
      </Section>

      <Section title="Paginación" snippet='<Pagination page totalPages total pageSize onPage />'>
        <Pagination page={demoPage} totalPages={4} total={52} pageSize={15}
          onPage={setDemoPage} noun="registros" />
      </Section>

      <Section title="Botones" snippet='<Button variant="primary" />'>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="success">Success</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="link">Link</Button>
          <Button variant="primary" size="sm">Small</Button>
          <Button variant="secondary" disabled>Disabled</Button>
        </div>
      </Section>

      <Section title="Badges" snippet='<Badge variant="publicado" />'>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {BADGES.map((v) => <Badge key={v} variant={v}>{v}</Badge>)}
        </div>
      </Section>

      <Section title="Stat Cards" snippet='<StatCard label value tone />'>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <StatCard label="Total" value="128" />
          <StatCard label="Activos" value="96" tone="success" />
          <StatCard label="Pendientes" value="24" tone="warning" />
          <StatCard label="Vencidos" value="8" tone="danger" />
        </div>
      </Section>

      <Section title="Toolbar + Form Fields" snippet='<Toolbar> <FormField>'>
        <Toolbar>
          <input className="form-input" placeholder="Buscar…" style={{ maxWidth: 260 }} />
          <select className="form-select" style={{ maxWidth: 200 }}>
            <option>Todos los estados</option>
            <option>Activo</option>
            <option>Borrador</option>
          </select>
          <Button variant="secondary">Filtrar</Button>
        </Toolbar>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
          <FormField label="Nombre" hint="Como aparece en documentos">
            <input className="form-input" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Escribe…" />
          </FormField>
          <FormField label="Categoría">
            <select className="form-select"><option>Opción A</option><option>Opción B</option></select>
          </FormField>
        </div>
      </Section>

      <Section title="Data Table" snippet='<DataTable columns rows rowKey />'>
        <DataTable
          columns={columns}
          rows={DEMO_ROWS}
          rowKey={(r) => r.id}
          empty="Sin registros."
        />
      </Section>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Modal de ejemplo"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => setModalOpen(false)}>Confirmar</Button>
          </>
        }
      >
        <p style={{ color: 'var(--text-secondary)' }}>
          Contenido del modal. Se cierra con la ×, el botón Cancelar o click en el fondo.
        </p>
      </Modal>
    </div>
  )
}
